import React, {
    createContext,
    useState,
    useEffect,
    ReactNode,
    useContext,
    useCallback,
    useMemo,
} from "react";

interface NetworkContextType {
    isOnline: boolean;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const NetworkProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

    const updateNetworkStatus = useCallback(() => {
        setIsOnline(navigator.onLine);
    }, []);

    useEffect(() => {
        // Set up event listeners
        window.addEventListener("online", updateNetworkStatus);
        window.addEventListener("offline", updateNetworkStatus);

        return () => {
            window.removeEventListener("online", updateNetworkStatus);
            window.removeEventListener("offline", updateNetworkStatus);
        };
    }, [updateNetworkStatus]);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        isOnline,
    }), [isOnline]);

    return (
        <NetworkContext.Provider value={contextValue}>
            {children}
        </NetworkContext.Provider>
    );
};

// Custom hook to consume the context
export const useNetwork = () => {
    const context = useContext(NetworkContext);
    if (!context) {
        throw new Error("useNetwork must be used within a NetworkProvider");
    }
    return context;
}; 